import { memo, useCallback, useEffect } from 'react'

import { Container } from '@cleartrip/ct-design-components'

import { ravenSDKTrigger } from '@/analytics/raven/ravenSDKHelper'
import UnlockEliteBanner from '@/components/home/UnlockEliteBanner'
import {
    LOYALTY_ACTION_NAME,
    LOYALTY_RAVEN_EVENT,
    SRP_PAGE_NAME
} from '@/constants/components/loyaltyAnalytics'
import { getSrpSearchData } from '@/redux/slices/srpSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store/hooks'
import { ActionMapper } from '@/types'
import type { V2SearchResponse } from '@/types/V2SearchTypes'
import { getActionPayload } from '@/utils/general/actions'
import { isUserSignedIn } from '@/utils/general/user'
import { getSrpLoyaltyAttributes, getSrpSearchAttributes } from '@/utils/srp/loyaltyAnalyticsUtils'
import { getUnlockElitePillState } from '@/utils/srp/loyaltySearchUtils'
import { setLoyaltyLoginContext } from '@/utils/loyalty/loyaltyLoginContext'
import type { ProcessedData } from '@/utils/srp/types'

type Props = {
    processedData?: Partial<ProcessedData>
    isSearchLoading?: boolean
}

// The banner sits in a virtualized list and can remount on scroll, so track
// fired impressions by searchId to keep it to one per search.
const firedImpressionSearchIds = new Set<string>()

const SrpUnlockEliteBanner = ({ processedData, isSearchLoading = false }: Props) => {
    const dispatch = useAppDispatch()
    const { searchData } = useAppSelector(getSrpSearchData)
    // programContext is nested on the search/v2 response stored in redux
    const programContextData = searchData?.programContext
        ? ({ programContext: searchData.programContext } as V2SearchResponse)
        : undefined
    const pillState = getUnlockElitePillState(programContextData, isUserSignedIn(), isSearchLoading)

    const buildLoyaltyPayload = useCallback(
        () => ({
            ...getSrpLoyaltyAttributes(programContextData),
            ...getSrpSearchAttributes(processedData, searchData),
            page_name: SRP_PAGE_NAME
        }),
        [programContextData, processedData, searchData]
    )

    const searchId = searchData?.searchId || ''
    useEffect(() => {
        if (!pillState || firedImpressionSearchIds.has(searchId)) return
        firedImpressionSearchIds.add(searchId)
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.WIDGET_IMPRESSION, buildLoyaltyPayload())
    }, [pillState, searchId, buildLoyaltyPayload])

    const handleLoginClick = useCallback(() => {
        const payload = buildLoyaltyPayload()
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.UI_ACTION, {
            ...payload,
            action_name: LOYALTY_ACTION_NAME.LOGGED_OUT_WIDGET_CLICK
        })
        // pass loyalty context to the login modal
        setLoyaltyLoginContext(payload)
        dispatch(getActionPayload('OPEN_MODAL', { type: ActionMapper.OPEN_LOG_IN_MODAL }))
    }, [buildLoyaltyPayload, dispatch])

    const handleArrowClick = useCallback(() => {
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.UI_ACTION, {
            ...buildLoyaltyPayload(),
            action_name: LOYALTY_ACTION_NAME.LOGGED_IN_WIDGET_CLICK
        })
    }, [buildLoyaltyPayload])

    const handleBenefitsPopupLoad = useCallback(() => {
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.BENEFITS_BOTTOMSHEET_LOAD, buildLoyaltyPayload())
    }, [buildLoyaltyPayload])

    const handleViewBenefitsClick = useCallback(() => {
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.UI_ACTION, {
            ...buildLoyaltyPayload(),
            action_name: LOYALTY_ACTION_NAME.BENEFITS_VIEW_CLICK
        })
    }, [buildLoyaltyPayload])

    const handleBenefitsPopupDismiss = useCallback(() => {
        ravenSDKTrigger(LOYALTY_RAVEN_EVENT.UI_ACTION, {
            ...buildLoyaltyPayload(),
            action_name: LOYALTY_ACTION_NAME.BENEFITS_DISMISS_CLICK
        })
    }, [buildLoyaltyPayload])

    if (!pillState) {
        return null
    }

    return (
        <Container className="mt-8 mb-6">
            <UnlockEliteBanner
                stripLabel={pillState.stripLabel}
                showLogin={pillState.showLogin}
                changeDateFee={pillState.changeDateFee}
                onLoginClick={handleLoginClick}
                onArrowClick={handleArrowClick}
                enableUnlockSoonModal
                onBenefitsPopupLoad={handleBenefitsPopupLoad}
                onViewBenefitsClick={handleViewBenefitsClick}
                onBenefitsPopupDismiss={handleBenefitsPopupDismiss}
            />
        </Container>
    )
}

export default memo(SrpUnlockEliteBanner)
